import type { SalaryContextAnalysis } from './buildSalaryContextAnalysis';

type SalaryNarrativeLocale = 'en' | 'fi' | 'sv';

interface SalaryNarrativeInput {
  locale: string;
  analysis: SalaryContextAnalysis;
  benchmarkLabel: string;
  jobRangeLabel?: string | null;
  formatCurrency: (value: number) => string;
  marketMin: number;
  marketMax: number;
  jobMin?: number | null;
  jobMax?: number | null;
}

function toLocale(locale: string): SalaryNarrativeLocale {
  if (locale === 'fi' || locale === 'sv') return locale;
  return 'en';
}

export function buildSalaryAnalysisNarrative(input: SalaryNarrativeInput): string {
  const locale = toLocale(input.locale);
  const {
    analysis,
    benchmarkLabel,
    jobRangeLabel,
    formatCurrency,
    marketMin,
    marketMax,
    jobMin,
    jobMax,
  } = input;

  const marketRange = `${formatCurrency(marketMin)}–${formatCurrency(marketMax)}`;
  const jobRange =
    typeof jobMin === 'number' && typeof jobMax === 'number'
      ? `${formatCurrency(jobMin)}–${formatCurrency(jobMax)}`
      : null;

  if (locale === 'fi') {
    const lead = analysis.differencePercent === null
      ? `Tarjouspalkkaa ei ole ilmoitettu tarkkana vaihteluvälinä, joten vertailu perustuu markkinadataan (${benchmarkLabel}).`
      : `Palkkatarjous sijoittuu vertailutasoon "${benchmarkLabel}" nähden: palkkahaarukan keskipiste on noin ${Math.abs(analysis.differencePercent)} % ${analysis.differencePercent >= 0 ? 'vertailutason yläpuolella' : 'vertailutason alapuolella'}.`;
    const position =
      analysis.placement === 'within'
        ? `Kokonaisuutena tarjous on kuitenkin edelleen markkinahaarukan (${marketRange}) sisällä.`
        : analysis.placement === 'above'
          ? `Kokonaisuutena tarjous asettuu markkinahaarukan (${marketRange}) yläpuolelle.`
          : analysis.placement === 'below'
            ? `Kokonaisuutena tarjous asettuu markkinahaarukan (${marketRange}) alapuolelle.`
            : `Sijoittumista markkinahaarukkaan (${marketRange}) ei voida arvioida täysin varmuudella.`;
    const spread =
      analysis.spreadComparison === 'tighter'
        ? `Tarjottu palkkaväli${jobRange ? ` (${jobRange})` : ''} on markkinaa kapeampi, mikä viittaa pienempään vaihteluun.`
        : analysis.spreadComparison === 'wider'
          ? `Tarjottu palkkaväli${jobRange ? ` (${jobRange})` : ''} on markkinaa leveämpi, mikä viittaa suurempaan vaihteluun.`
          : analysis.spreadComparison === 'similar'
            ? `Tarjotun palkkavälin leveys on lähellä markkinan tasoa.`
            : `Palkkavälin leveyttä ei voi verrata luotettavasti markkinaan.`;
    const sample =
      analysis.sampleSize !== null
        ? `Vertailu perustuu ${analysis.sampleSize} vastaavaan ilmoitukseen.`
        : '';
    return [lead, position, spread, sample].filter(Boolean).join(' ');
  }

  if (locale === 'sv') {
    const lead = analysis.differencePercent === null
      ? `Löneerbjudandet saknar ett tydligt intervall, så jämförelsen bygger främst på marknadsdata (${benchmarkLabel}).`
      : `Löneerbjudandet i den här annonsen jämfört med referensen "${benchmarkLabel}": mittpunkten ligger cirka ${Math.abs(analysis.differencePercent)} % ${analysis.differencePercent >= 0 ? 'över' : 'under'} referensnivån.`;
    const position =
      analysis.placement === 'within'
        ? `Samtidigt ligger erbjudandet fortfarande inom marknadsintervallet (${marketRange}).`
        : analysis.placement === 'above'
          ? `Samtidigt ligger erbjudandet över marknadsintervallet (${marketRange}).`
          : analysis.placement === 'below'
            ? `Samtidigt ligger erbjudandet under marknadsintervallet (${marketRange}).`
            : `Placeringen i förhållande till marknadsintervallet (${marketRange}) kan inte fastställas säkert.`;
    const spread =
      analysis.spreadComparison === 'tighter'
        ? `Erbjudandets löneintervall${jobRange ? ` (${jobRange})` : ''} är smalare än marknaden, vilket tyder på mindre variation.`
        : analysis.spreadComparison === 'wider'
          ? `Erbjudandets löneintervall${jobRange ? ` (${jobRange})` : ''} är bredare än marknaden, vilket tyder på större variation.`
          : analysis.spreadComparison === 'similar'
            ? `Intervallbredden är i stort sett i linje med marknaden.`
            : `Intervallbredden kan inte jämföras tillförlitligt med marknaden.`;
    const sample =
      analysis.sampleSize !== null
        ? `Referensen bygger på ${analysis.sampleSize} jämförbara annonser.`
        : '';
    return [lead, position, spread, sample].filter(Boolean).join(' ');
  }

  const lead = analysis.differencePercent === null
    ? `No explicit offer band is available, so this comparison uses the market benchmark (${benchmarkLabel}).`
    : `Compared with the selected benchmark ("${benchmarkLabel}"), this listing's salary midpoint is about ${Math.abs(analysis.differencePercent)}% ${analysis.differencePercent >= 0 ? 'higher' : 'lower'}.`;
  const position =
    analysis.placement === 'within'
      ? `The offer still falls within the benchmark range (${marketRange}).`
      : analysis.placement === 'above'
        ? `The offer sits above the benchmark range (${marketRange}).`
        : analysis.placement === 'below'
          ? `The offer sits below the benchmark range (${marketRange}).`
          : `The exact position against the benchmark range (${marketRange}) cannot be determined reliably.`;
  const spread =
    analysis.spreadComparison === 'tighter'
      ? `The listed pay band${jobRange ? ` (${jobRange})` : ''} is tighter than the benchmark, which suggests lower salary variability.`
      : analysis.spreadComparison === 'wider'
        ? `The listed pay band${jobRange ? ` (${jobRange})` : ''} is wider than the benchmark, which suggests greater salary variability.`
        : analysis.spreadComparison === 'similar'
          ? `The offer's range width is broadly in line with the benchmark.`
          : `Range-width comparison is limited because one of the salary bands is incomplete.`;
  const sample =
    analysis.sampleSize !== null
      ? `This benchmark is based on ${analysis.sampleSize} comparable listings.`
      : '';
  return [lead, position, spread, sample].filter(Boolean).join(' ');
}
